/**
 * Provider/gateway error classification — shared single source of truth
 * for *both* the chat workflow (which uses this to upgrade a generic
 * `stream_error` into a specific `MessageErrorCode` before persisting)
 * and the chat UI (which uses this on transport-level errors that fire
 * before any metadata gets persisted).
 *
 * Co-locating the classifier with the user-facing copy (`PROVIDER_ERROR_COPY`)
 * keeps the title/body for every provider failure mode in one place — the
 * workflow's `ERROR_MESSAGES` map and the in-chat error banner both spread
 * from it, so adding a new provider code automatically updates both surfaces.
 *
 * MUST stay free of Node-only imports — this module is reachable from
 * `apps/web/workflows/chat/workflow.ts` (durable workflow bundle, no `pg`/
 * `fs`/OTel allowed) *and* from React client components, including the
 * published `magnus-chat` block (via `@bg-bgi/magnus-sdk`).
 * `@workspace/ai/classify-provider-error` re-exports it.
 *
 * Sources of signal (checked in this order, walking the `cause` chain):
 *   0. The gateway's ZDR routing failure, matched on its text signature in a
 *      pre-pass over the whole chain — it has no dedicated error class, so the
 *      generic passes below misread it (see `ZDR_ROUTING_FAILURE`).
 *   0b. Our own infrastructure clients (ioredis) failing mid-stream — a
 *      pre-pass that *refuses* to classify, so a Redis stall is never blamed
 *      on the provider (see `isInfrastructureError`).
 *   1. Vercel AI Gateway typed errors — duck-typed via `name` so we don't
 *      need to import the `@ai-sdk/gateway` class hierarchy. The gateway
 *      sets `name` to a stable string (`"GatewayRateLimitError"`,
 *      `"GatewayInternalServerError"`, …).
 *   2. AI SDK `APICallError` / arbitrary provider errors — fall back to
 *      the numeric HTTP `statusCode` / `status` field.
 */

import type { MessageErrorCode } from "./messages";

/**
 * The subset of `MessageErrorCode` that this classifier can produce.
 * Narrowing the return type lets the UI safely index `PROVIDER_ERROR_COPY`
 * without an exhaustive `MessageErrorCode` switch.
 */
export type ProviderErrorCode = Extract<
  MessageErrorCode,
  | "provider_unavailable"
  | "provider_rate_limit"
  | "provider_auth_error"
  | "provider_bad_request"
  | "provider_timeout"
  | "model_unavailable"
>;

/**
 * Single source of truth for the title (scannable headline) + body
 * (longer, action-oriented explanation) shown to users when a provider
 * failure is identified.
 *
 * Consumed by:
 *   - `apps/web/workflows/chat/workflow.ts` → spreads `body` into
 *     `ERROR_MESSAGES` so the persisted message metadata carries the
 *     same copy as the in-flight banner.
 *   - `apps/web/components/chat/error-tiers.ts` → exposes `title` via
 *     `getErrorTitle()` for both the per-message and transport-level
 *     error indicators.
 */
export const PROVIDER_ERROR_COPY: Record<
  ProviderErrorCode,
  { title: string; body: string }
> = {
  provider_unavailable: {
    title: "Provider temporarily unavailable",
    body: "The AI provider is currently unavailable. Please try again in a moment, or pick a different model.",
  },
  provider_rate_limit: {
    title: "Rate limited",
    body: "The AI provider is rate-limiting requests right now. Please wait a moment and try again.",
  },
  provider_auth_error: {
    title: "Provider authentication failed",
    body: "We couldn't authenticate with the AI provider. Please contact support if this keeps happening.",
  },
  provider_bad_request: {
    title: "Request rejected by provider",
    body: "The AI provider rejected this request. Try rephrasing your message or starting a new chat.",
  },
  provider_timeout: {
    title: "Provider took too long to respond",
    body: "The AI provider took too long to respond. Please try again, or pick a different model.",
  },
  model_unavailable: {
    title: "Model currently unavailable",
    body: "This model is currently unavailable. Please pick a different model.",
  },
};

/** Defense-in-depth bound on `cause`-chain traversal — cyclic causes
 *  would otherwise stack-overflow the workflow worker. */
const MAX_CAUSE_DEPTH = 6;

/**
 * The gateway's ZDR routing failure: every provider that could serve the
 * requested model lacks a zero-data-retention agreement with Vercel, so the
 * request is refused with a deterministic HTTP 400 before any inference.
 *
 * Needs its own signature because the gateway SDK has no error class for it —
 * `type: "no_zdr_providers_available"` falls through
 * `createGatewayErrorFromResponse`'s default branch to a
 * `GatewayInternalServerError`, whose name reads as a transient outage and whose
 * 400 reads as a malformed prompt. Matches both the `type` slug (what survives
 * into a serialized envelope) and the human-readable message.
 *
 * @see https://vercel.com/docs/ai-gateway/security-and-compliance/zdr
 */
const ZDR_ROUTING_FAILURE =
  /no_zdr_providers_available|no zdr \(zero data retention\) providers/i;

/**
 * Errors raised by *our own* infrastructure clients while a turn is
 * streaming — not by the AI provider or the gateway. These must never be
 * upgraded to a `ProviderErrorCode`: the free-text pass below would read
 * ioredis's `Command timed out` as a provider timeout and tell the user to
 * "pick a different model" for a Redis stall on our side (2026-09-25: nine
 * turns mislabelled `provider_timeout` in one morning, all of them the
 * workflow belt write timing out on a replica whose event loop was blocked).
 *
 * Matched on the exact message strings ioredis throws plus its stack-frame
 * path, since by the time the error reaches the classifier it is usually a
 * bare `Error` with no typed `name`.
 */
const REDIS_CLIENT_MESSAGE =
  /^Command timed out$|^Stream isn't writeable|^Connection is closed\.?$|^Reached the max retries per request limit/;
const REDIS_CLIENT_STACK = /[\\/]ioredis[\\/]/;

function isInfrastructureError(err: object): boolean {
  const message = (err as { message?: unknown }).message;
  if (typeof message === "string" && REDIS_CLIENT_MESSAGE.test(message)) {
    return true;
  }
  const stack = (err as { stack?: unknown }).stack;
  return typeof stack === "string" && REDIS_CLIENT_STACK.test(stack);
}

/**
 * Returns a `ProviderErrorCode` for `err` when the error shape lets us
 * identify a provider/gateway failure mode. Returns `undefined` when the
 * error is unrecognized — the caller should fall back to its generic
 * "stream_error" / "Failed to get response" copy.
 */
export function classifyProviderError(
  err: unknown
): ProviderErrorCode | undefined {
  if (err == null) {
    return undefined;
  }
  const chain = collectCauseChain(err);

  // ZDR routing failures get a pre-pass over the whole chain instead of riding
  // the per-error walk below. The AI SDK / workflow transport wraps them in an
  // error whose own text ("Error processing UI message chunks") matches this
  // module's 5xx patterns, so a strictly outside-in walk answers "provider
  // temporarily unavailable" from the wrapper and never reaches the
  // deterministic refusal underneath — telling the user to retry a request that
  // cannot succeed. The signature is specific enough that finding it anywhere in
  // the chain is conclusive.
  if (chain.some((cur) => ZDR_ROUTING_FAILURE.test(readErrorText(cur)))) {
    return "model_unavailable";
  }

  // Infrastructure failures (Redis client) anywhere in the chain are
  // conclusive the other way: this is not a provider outcome, so return
  // `undefined` and let the caller fall back to its generic `stream_error`.
  // Checked before the per-error walk because the transport wrapper around
  // such an error ("Error processing UI message chunks") would otherwise
  // classify as `provider_unavailable` from the outside in.
  if (chain.some(isInfrastructureError)) {
    return undefined;
  }

  for (const cur of chain) {
    const classified = classifyOne(cur);
    if (classified) {
      return classified;
    }
  }
  return undefined;
}

/**
 * The error and its transitive `cause`s, outermost first. Bounded by
 * {@link MAX_CAUSE_DEPTH} and de-duplicated so a cyclic `cause` terminates.
 * Non-object links end the chain: they carry no `cause` and no field any
 * classifier here reads.
 */
function collectCauseChain(err: unknown): object[] {
  const seen = new WeakSet<object>();
  const chain: object[] = [];
  let cur: unknown = err;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH && cur != null; depth++) {
    if (typeof cur !== "object" || seen.has(cur)) {
      break;
    }
    seen.add(cur);
    chain.push(cur);
    cur = (cur as { cause?: unknown }).cause;
  }
  return chain;
}

function classifyOne(err: unknown): ProviderErrorCode | undefined {
  if (!err || typeof err !== "object") {
    return undefined;
  }

  // 1. Vercel AI Gateway typed errors — match by `name` so we don't have
  //    to import the class hierarchy. Names are documented in
  //    `@ai-sdk/gateway`'s public type surface.
  const name = (err as { name?: unknown }).name;
  if (typeof name === "string") {
    switch (name) {
      case "GatewayRateLimitError":
        return "provider_rate_limit";
      case "GatewayAuthenticationError":
        return "provider_auth_error";
      case "GatewayInternalServerError":
      case "GatewayResponseError": {
        // The gateway wraps *upstream* provider failures in these
        // "internal server error"-shaped classes even when the upstream
        // response was a deterministic 4xx — e.g. Anthropic
        // request-validation rejections ("`thinking` … blocks cannot be
        // modified", "prompt is too long") arrive as a
        // `GatewayInternalServerError` with `statusCode: 400`. Presenting
        // those as "temporarily unavailable, try again" sends users into
        // retry loops on requests that can never succeed, so trust the
        // wrapped HTTP status when it identifies a specific failure mode.
        // See docs/incidents/2026-07-11-provider-unavailable-thinking-blocks.md
        // (upstream issue U1 — the mislabel is a gateway-service bug).
        const status = readHttpStatus(err);
        const fromStatus =
          status === undefined ? undefined : classifyHttpStatus(status);
        return fromStatus ?? "provider_unavailable";
      }
      case "GatewayInvalidRequestError":
        return "provider_bad_request";
      case "GatewayModelNotFoundError":
        return "model_unavailable";
      default:
        break;
    }
  }

  // 2. AI SDK `APICallError` and similar — fall back to numeric HTTP status.
  const rawStatus = readHttpStatus(err);
  if (rawStatus !== undefined) {
    const code = classifyHttpStatus(rawStatus);
    if (code) {
      return code;
    }
  }

  // 3. Last-resort: pattern-match against `error.message`. By the time
  //    an `agent.stream` error crosses the workflow → transport →
  //    `useChat` boundary, the typed `name` and `statusCode` fields have
  //    usually been stripped — all the client sees is `new Error("...")`
  //    with a free-text message. This layer recovers a `ProviderErrorCode`
  //    from the textual signal that survives that round-trip:
  //      3a. A JSON-stringified OpenAI streaming error envelope (the
  //          format the gateway forwards verbatim when the upstream
  //          provider sends an SSE `error` event) → classify on the
  //          inner `error.type` against OpenAI's well-known taxonomy.
  //      3b. Free-text patterns (gateway-written errorText chunks,
  //          sanitized HTTP status phrases, AI SDK transport wrappers).
  const message = (err as { message?: unknown }).message;
  if (typeof message === "string") {
    return classifyMessageString(message);
  }

  return undefined;
}

/**
 * All free-text an error carries, for signature matching. `message` holds the
 * gateway's human-readable text; `responseBody` is the raw upstream payload the
 * AI SDK preserves on `APICallError`, and is where a machine-readable `type`
 * slug survives when a wrapper has overwritten `message` with its own prose.
 */
function readErrorText(err: object): string {
  const message = (err as { message?: unknown }).message;
  const responseBody = (err as { responseBody?: unknown }).responseBody;
  return [message, responseBody]
    .filter((v) => typeof v === "string")
    .join("\n");
}

/** The numeric HTTP status carried by an error, if any. Different libs use
 *  different field names (`statusCode` vs `status`), so check both. */
function readHttpStatus(err: object): number | undefined {
  const raw =
    (err as { statusCode?: unknown }).statusCode ??
    (err as { status?: unknown }).status;
  return typeof raw === "number" ? raw : undefined;
}

function classifyHttpStatus(status: number): ProviderErrorCode | undefined {
  if (status === 429) {
    return "provider_rate_limit";
  }
  if (status === 401 || status === 403) {
    return "provider_auth_error";
  }
  if (status === 404) {
    return "model_unavailable";
  }
  if (status === 408 || status === 504) {
    return "provider_timeout";
  }
  if (status === 400) {
    return "provider_bad_request";
  }
  if (status >= 500) {
    return "provider_unavailable";
  }
  return undefined;
}

/**
 * String-based classification — the catch-all when structured error
 * metadata (gateway name, HTTP status) didn't survive serialization.
 *
 * Runs two passes:
 *   1. Try to parse `message` as a JSON envelope and classify on the
 *      inner `error.type` if it matches OpenAI's well-known taxonomy.
 *      The gateway forwards upstream SSE error chunks verbatim, so most
 *      "Azure GPT-5.5 just 500'd" failures arrive shaped like
 *      `{"type":"error","error":{"type":"server_error","message":"…"}}`
 *      — invisible to plain regex matching.
 *   2. Fall through to free-text regex matching for everything else
 *      (gateway errorText chunks, sanitized HTTP status phrases, AI SDK
 *      transport wrappers).
 */
function classifyMessageString(message: string): ProviderErrorCode | undefined {
  const fromEnvelope = classifyOpenAIErrorEnvelope(message);
  if (fromEnvelope) {
    return fromEnvelope;
  }
  return classifyMessageRegex(message);
}

/**
 * OpenAI error-envelope shape (matches both REST and streaming SSE chunks):
 *   `{ type?: "error", error: { type: string, message?: string, ... } }`
 * `tryParseEnvelope` returns undefined when the message isn't JSON or
 * doesn't have the expected shape; the caller falls back to regex.
 */
type OpenAIErrorEnvelope = {
  type?: string;
  error?: { type?: string; message?: string };
};

function classifyOpenAIErrorEnvelope(
  message: string
): ProviderErrorCode | undefined {
  const trimmed = message.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) {
    return undefined;
  }
  let parsed: OpenAIErrorEnvelope;
  try {
    parsed = JSON.parse(trimmed) as OpenAIErrorEnvelope;
  } catch {
    return undefined;
  }
  const innerType = parsed.error?.type;
  const codeFromType =
    typeof innerType === "string"
      ? classifyOpenAIErrorType(innerType)
      : undefined;
  if (codeFromType) {
    return codeFromType;
  }
  // If `error.type` was missing or unrecognized, recurse on the inner
  // message text — providers sometimes set `type: "error"` (generic) but
  // put the real signal in the human-readable string.
  const innerMessage = parsed.error?.message;
  if (typeof innerMessage === "string" && innerMessage !== message) {
    return classifyMessageRegex(innerMessage);
  }
  return undefined;
}

/**
 * Maps OpenAI's documented `error.type` values to our internal code set.
 * Source: https://platform.openai.com/docs/guides/error-codes/api-errors
 * Also covers Azure OpenAI variants which mirror the same taxonomy.
 */
function classifyOpenAIErrorType(type: string): ProviderErrorCode | undefined {
  switch (type) {
    case "server_error":
    case "service_unavailable":
      return "provider_unavailable";
    case "rate_limit_exceeded":
    case "tokens":
    case "requests":
    case "insufficient_quota":
      return "provider_rate_limit";
    case "invalid_request_error":
    case "invalid_request":
      return "provider_bad_request";
    case "authentication_error":
    case "invalid_api_key":
    case "permission_error":
      return "provider_auth_error";
    case "model_not_found":
    case "not_found":
      return "model_unavailable";
    case "timeout":
    case "request_timeout":
      return "provider_timeout";
    default:
      return undefined;
  }
}

/**
 * Free-text regex matcher — runs when the message isn't a JSON envelope
 * (or the envelope didn't yield a code). Order matters: more specific
 * phrases first so e.g. "rate limit" isn't shadowed by a generic "500"
 * check that doesn't apply.
 */
function classifyMessageRegex(message: string): ProviderErrorCode | undefined {
  const m = message.toLowerCase();

  if (/\brate.?limit|too many requests|429\b/.test(m)) {
    return "provider_rate_limit";
  }
  if (/\b(unauthorized|forbidden|401|403)\b|authentication/.test(m)) {
    return "provider_auth_error";
  }
  if (/\b404\b|model.*not.*found|model.*unavailable/.test(m)) {
    return "model_unavailable";
  }
  if (/\b(timeout|timed out|deadline exceeded|408|504)\b/.test(m)) {
    return "provider_timeout";
  }
  // Anthropic request-validation rejections forwarded through the gateway —
  // deterministic 400s whose text is the only signal that survives
  // serialization. Checked before the generic "unavailable" patterns so a
  // wrapped stack trace can't shadow them.
  if (
    /blocks in the latest assistant message cannot be modified|prompt is too long|text content blocks must be non-empty|image dimensions exceed/.test(
      m
    )
  ) {
    return "provider_bad_request";
  }
  if (
    /\b(internal server error|bad gateway|service unavailable|500|502|503)\b/.test(
      m
    ) ||
    // Vercel AI SDK / Workflow transport-level error wrappers that fire
    // when the upstream stream errors mid-flight or returns a 5xx.
    /error processing ui message chunks|stream (closed|aborted|errored)/.test(
      m
    ) ||
    // OpenAI / Azure server_error stock message — typically
    // "…include request ID req_abc123 in your email." The phrase is
    // stable across both REST and streaming SSE responses.
    /include req_[a-z0-9-]+\s+in your email|incident id/.test(m)
  ) {
    return "provider_unavailable";
  }
  if (/\b(invalid|malformed|bad request|400)\b/.test(m)) {
    return "provider_bad_request";
  }
  return undefined;
}
